type McpBridgeConfig = {
  baseUrl: string
  token?: string
}

export type McpToolContent = { type: string; text?: string }
export type McpToolCallResponse = { content: McpToolContent[] }

export type McpResourceContent = { uri: string; text?: string; mimeType?: string }
export type McpResourceReadResponse = { contents: McpResourceContent[] }

const DEFAULT_BASE_URL = 'http://localhost:4000'

const resolveEnv = () => {
  const env =
    typeof import.meta !== 'undefined' ? (import.meta as unknown as { env?: Record<string, string> })?.env : undefined
  const url = env?.VITE_MCP_BRIDGE_URL ?? process.env.VITE_MCP_BRIDGE_URL
  const token = env?.VITE_MCP_BRIDGE_TOKEN ?? process.env.VITE_MCP_BRIDGE_TOKEN
  return { url, token }
}

export const getMcpBridgeConfig = (): McpBridgeConfig => {
  const { url, token } = resolveEnv()
  const trimmedBase = (url ?? DEFAULT_BASE_URL).replace(/\/+$/, '')
  const trimmedToken = token?.trim()
  return { baseUrl: trimmedBase, token: trimmedToken && trimmedToken.length > 0 ? trimmedToken : undefined }
}

export const getMcpBridgeDebug = () => {
  const cfg = getMcpBridgeConfig()
  return { baseUrl: cfg.baseUrl, hasToken: !!cfg.token }
}

const withAuthHeaders = (config: McpBridgeConfig) => {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (config.token) headers.Authorization = `Bearer ${config.token}`
  return headers
}

const handleResponse = async <T>(response: Response): Promise<T> => {
  const bodyText = await response.text()
  const asJson = bodyText
    ? (() => {
        try {
          return JSON.parse(bodyText)
        } catch {
          return undefined
        }
      })()
    : undefined
  if (!response.ok) {
    const detail = asJson ?? bodyText
    throw new Error(
      `MCP bridge error ${response.status}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`
    )
  }
  return (asJson ?? (bodyText as unknown)) as T
}

export const callBridgeTool = async (
  name: string,
  args: Record<string, unknown> = {},
  config: McpBridgeConfig = getMcpBridgeConfig()
): Promise<McpToolCallResponse> => {
  const response = await fetch(`${config.baseUrl}/tools/call`, {
    method: 'POST',
    headers: withAuthHeaders(config),
    body: JSON.stringify({ name, args }),
  })
  return handleResponse<McpToolCallResponse>(response)
}

export const listBridgeTools = async (
  config: McpBridgeConfig = getMcpBridgeConfig()
): Promise<{ tools: Array<{ name: string }> }> => {
  if (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, unknown> }).env?.DEV) {
    console.debug('listBridgeTools', { baseUrl: config.baseUrl, hasToken: !!config.token })
  }
  const response = await fetch(`${config.baseUrl}/tools`, {
    headers: config.token ? { Authorization: `Bearer ${config.token}` } : undefined,
  })
  return handleResponse<{ tools: Array<{ name: string }> }>(response)
}

export const readBridgeResource = async (
  uri: string,
  config: McpBridgeConfig = getMcpBridgeConfig()
): Promise<McpResourceReadResponse> => {
  const response = await fetch(`${config.baseUrl}/resources/read?uri=${encodeURIComponent(uri)}`, {
    headers: config.token ? { Authorization: `Bearer ${config.token}` } : undefined,
  })
  return handleResponse<McpResourceReadResponse>(response)
}

export const listBridgeResources = async (
  config: McpBridgeConfig = getMcpBridgeConfig()
): Promise<{ resources: Array<{ uriTemplate: string }> }> => {
  const response = await fetch(`${config.baseUrl}/resources`, {
    headers: config.token ? { Authorization: `Bearer ${config.token}` } : undefined,
  })
  return handleResponse<{ resources: Array<{ uriTemplate: string }> }>(response)
}
